import { Check, MapPin, Trophy } from "lucide-react";
import { AnimatePresence, LayoutGroup, motion } from "motion/react";
import { useId } from "react";
import {
	ExampleTag,
	PhoneFrame,
	shotProps,
} from "@/components/blocks/frames/Frames";
import { DURATION, EASE, SPRING } from "@/components/motion/motion";
import { useSceneLoop } from "@/hooks/use-scene-loop";
import { useI18n } from "@/lib/i18n/use-i18n";
import { cn } from "@/lib/utils";

const ROUTE_DONE = "M30 225 C50 200 60 170 92 155 C122 140 142 110 130 78";
const ROUTE_NEXT = "M130 78 C122 52 150 36 172 22";
const PINS = [
	{ x: 30, y: 225, state: "done" },
	{ x: 92, y: 155, state: "done" },
	{ x: 130, y: 78, state: "current" },
	{ x: 172, y: 22, state: "next" },
] as const;

/** Stylised town map with the game route. Deterministic markup (SSR-safe). */
export function QuestMap({ className }: { className?: string }) {
	const id = useId().replace(/[^a-zA-Z0-9_-]/g, "");
	const routeId = `quest-route-${id}`;

	return (
		<svg
			viewBox="0 0 200 240"
			preserveAspectRatio="xMidYMid slice"
			className={cn("h-full w-full", className)}
			aria-hidden="true"
		>
			<rect width="200" height="240" className="fill-muted" />
			<rect
				x="78"
				y="52"
				width="54"
				height="42"
				rx="8"
				className="fill-foreground/[0.06]"
			/>
			<rect
				x="8"
				y="150"
				width="42"
				height="36"
				rx="8"
				className="fill-foreground/[0.06]"
			/>
			<rect
				x="150"
				y="160"
				width="44"
				height="60"
				rx="8"
				className="fill-foreground/[0.04]"
			/>
			<g
				className="stroke-background"
				strokeWidth="7"
				strokeLinecap="round"
				fill="none"
			>
				<path d="M-10 62 L210 40" />
				<path d="M-10 132 L210 150" />
				<path d="M-10 204 L210 186" />
				<path d="M52 -10 L72 250" />
				<path d="M150 -10 L120 250" />
				<path d="M-10 98 Q100 114 210 92" />
			</g>
			<path id={routeId} d={ROUTE_DONE} fill="none" stroke="none" />
			<path
				d={ROUTE_NEXT}
				fill="none"
				strokeWidth="3"
				strokeDasharray="2 6"
				strokeLinecap="round"
				className="stroke-foreground/30"
			/>
			<motion.path
				d={ROUTE_DONE}
				fill="none"
				strokeWidth="3.5"
				strokeLinecap="round"
				className="stroke-signal"
				initial={{ pathLength: 0 }}
				whileInView={{ pathLength: 1 }}
				viewport={{ once: true }}
				transition={{ duration: DURATION.draw, ease: EASE }}
			/>
			{PINS.map((pin) => (
				<g key={`${pin.x}-${pin.y}`} transform={`translate(${pin.x} ${pin.y})`}>
					{pin.state === "current" ? (
						<circle
							r="14"
							className="fill-signal/20 pulse-ring origin-center [transform-box:fill-box]"
						/>
					) : null}
					<circle
						r={pin.state === "current" ? 7 : 5}
						strokeWidth="2.5"
						className={cn(
							"stroke-background",
							pin.state === "next" ? "fill-foreground/35" : "fill-signal",
						)}
					/>
				</g>
			))}
			{/* Other teams travelling the route; hidden when motion is reduced. */}
			<g className="motion-reduce:hidden">
				{[0, -3.2].map((begin) => (
					<circle
						key={begin}
						r="3.5"
						className="fill-foreground stroke-background"
						strokeWidth="1.5"
					>
						<animateMotion
							dur="9s"
							begin={`${begin}s`}
							repeatCount="indefinite"
						>
							<mpath href={`#${routeId}`} />
						</animateMotion>
					</circle>
				))}
			</g>
		</svg>
	);
}

const QUEST_DURATIONS = [2600, 2800, 3600] as const;

/** Questovery-style game on a phone: approach → riddle → step validated. */
export function QuestPhone({
	index,
	className,
}: {
	index: number;
	className?: string;
}) {
	const { dict } = useI18n();
	const quest = dict.scenes.quest;

	return (
		<PhoneFrame tag={dict.common.illustrationBadge} className={className}>
			<div className="absolute inset-0">
				<QuestMap />
			</div>
			<div className="absolute inset-x-3 top-2 flex items-center justify-between rounded-2xl bg-background/90 px-3 py-2 shadow-sm backdrop-blur">
				<span className="truncate text-[11px] font-semibold">
					{quest.title}
				</span>
				<span className="shrink-0 rounded-full bg-foreground px-2 py-0.5 font-mono text-[9px] text-background">
					{quest.step}
				</span>
			</div>
			<div className="absolute inset-x-2 bottom-2 min-h-[8.5rem] overflow-hidden rounded-[1.4rem] bg-background p-4 shadow-[0_-8px_30px_-12px_oklch(0_0_0/0.25)]">
				<AnimatePresence mode="wait" initial={false}>
					<motion.div
						key={index}
						initial={{ opacity: 0, y: 12 }}
						animate={{ opacity: 1, y: 0 }}
						exit={{ opacity: 0, y: -8 }}
						transition={{ duration: 0.35, ease: EASE }}
					>
						{index === 0 ? (
							<div className="space-y-3">
								<p className="flex items-center gap-2 text-[11px] text-muted-foreground">
									<span className="relative flex size-2">
										<span className="pulse-ring absolute inset-0 rounded-full bg-signal" />
										<span className="relative size-2 rounded-full bg-signal" />
									</span>
									{quest.approach}
								</p>
								<p className="text-3xl font-semibold tracking-tight tabular-nums">
									{quest.distance}
								</p>
								<p className="flex items-center gap-1.5 text-xs font-medium">
									<MapPin className="size-3.5 text-signal-ink" />
									{quest.place}
								</p>
							</div>
						) : index === 1 ? (
							<div className="space-y-3">
								<p className="text-[13px] leading-snug font-semibold text-balance">
									{quest.question}
								</p>
								<div className="grid grid-cols-3 gap-2">
									{Object.entries(quest.answers).map(([key, answer]) => (
										<span
											key={key}
											className={cn(
												"rounded-xl border py-2 text-center text-sm font-semibold tabular-nums",
												key === "b" && "border-signal bg-signal/10",
											)}
										>
											{answer}
										</span>
									))}
								</div>
							</div>
						) : (
							<div className="flex items-center gap-3">
								<span className="flex size-12 shrink-0 items-center justify-center rounded-full bg-signal text-signal-foreground">
									<Check className="size-6" strokeWidth={3} />
								</span>
								<div>
									<p className="text-sm font-semibold">{quest.validated}</p>
									<p className="text-[11px] text-muted-foreground">
										{quest.place}
									</p>
									<p className="mt-1 font-mono text-lg font-semibold text-signal-ink">
										{quest.points}
									</p>
								</div>
							</div>
						)}
					</motion.div>
				</AnimatePresence>
			</div>
		</PhoneFrame>
	);
}

/** Leaderboard that reorders when "our" team validates its step. */
export function QuestRanking({
	validated,
	className,
}: {
	validated: boolean;
	className?: string;
}) {
	const { dict } = useI18n();
	const quest = dict.scenes.quest;
	const layoutId = useId();
	const teams = [
		{ key: "a", name: quest.teams.a, points: 840, ours: false },
		{
			key: "b",
			name: quest.teams.b,
			points: validated ? 960 : 780,
			ours: true,
		},
		{ key: "c", name: quest.teams.c, points: 610, ours: false },
	].sort((a, b) => b.points - a.points);

	return (
		<div
			{...shotProps}
			className={cn(
				"paper shot-shadow w-56 rounded-2xl border p-4 select-none",
				className,
			)}
		>
			<div className="mb-3 flex items-center justify-between">
				<p className="flex items-center gap-2 text-xs font-semibold">
					<Trophy className="size-3.5 text-signal-ink" />
					{quest.ranking}
				</p>
				<ExampleTag className="px-1.5 text-[8px]">
					{dict.common.illustrationBadge}
				</ExampleTag>
			</div>
			<LayoutGroup id={layoutId}>
				<ol className="space-y-1.5">
					{teams.map((team, position) => (
						<motion.li
							layout
							transition={SPRING}
							key={team.key}
							className={cn(
								"flex items-center gap-3 rounded-lg px-2 py-1.5 text-xs",
								team.ours && "bg-signal/10",
							)}
						>
							<span className="w-3 font-mono text-muted-foreground tabular-nums">
								{position + 1}
							</span>
							<span
								className={cn(
									"size-2 rounded-full",
									team.ours ? "bg-signal" : "bg-foreground/25",
								)}
							/>
							<span className="flex-1 truncate font-medium">{team.name}</span>
							<span className="font-mono tabular-nums">{team.points}</span>
						</motion.li>
					))}
				</ol>
			</LayoutGroup>
		</div>
	);
}

/** Phone game plus floating leaderboard. Server renders the validated step. */
export function QuestScene({
	className,
	ranking = true,
}: {
	className?: string;
	ranking?: boolean;
}) {
	const { ref, index } = useSceneLoop(QUEST_DURATIONS, { initial: 2 });

	return (
		<div ref={ref} className={cn("relative flex justify-center", className)}>
			<QuestPhone index={index} className="w-[15.5rem] sm:w-[17rem]" />
			{ranking ? (
				<QuestRanking
					validated={index === 2}
					className="absolute top-1/2 -right-2 hidden md:block lg:-right-6 xl:-right-12"
				/>
			) : null}
		</div>
	);
}
